import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnInit,
  Output,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import { FormGroup, FormBuilder, FormArray, Validators } from '@angular/forms';
import { DomSanitizer } from '@angular/platform-browser';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { ToastrService } from 'ngx-toastr';
import { forkJoin, of } from 'rxjs';
import { IGetCodes } from 'src/app/interfaces/codes';
import {
  IGetQuestionRelationshipResponse,
  IGroup,
  IQuestionRelationship,
  IQuestionsWithAnswersModifiedResponse,
} from 'src/app/interfaces/questionManagement';
import { QuestionManagementService } from 'src/app/Services/question-management.service';
import {
  CdkDragDrop,
  CdkDragEnter,
  CdkDragMove,
  moveItemInArray,
} from '@angular/cdk/drag-drop';
import { ConstantVars } from 'src/app/utils/constants/constants';
import { FlashCardManagementService } from 'src/app/Services/flashcard-management.service';
import { IFlashCard } from 'src/app/interfaces/flashCardManagement';
import { catchError } from 'rxjs/operators';

@Component({
  selector: 'app-shuffling-flashcard',
  templateUrl: './shuffling-flashcard.component.html',
  styleUrls: ['./shuffling-flashcard.component.css'],
  encapsulation: ViewEncapsulation.None,
})
export class ShufflingFlashcardComponent implements OnInit {
  @Input() SubjectId: number;
  @Input() FlashCardId: string | null;
  @Input() ScreenName: string;
  @Output() openFlashCard = new EventEmitter<any>(); // Define EventEmitter

  FlashCards: IFlashCard[] = [];
  @ViewChild('dropListContainer') dropListContainer?: ElementRef;
  dropListReceiverElement?: HTMLElement;
  dragDropInfo?: {
    dragIndex: number;
    dropIndex: number;
  };
  droppedIndex: number | null;
  displayOrder: boolean = false;
  path: string;

  constructor(
    public activeModal: NgbActiveModal,
    public flashcardService: FlashCardManagementService,
    private sanitizer: DomSanitizer,
    private readonly toaster: ToastrService,
  ) {}

  ngOnInit(): void {
    const payloadGetquestions = {
      SubjectId: this.SubjectId,
      FlashcardID: this.FlashCardId,
      ScreenName: this.ScreenName,
    };
    const pathPayload = {
      SubjectId: this.SubjectId,
      ScreenName: 'FLASHCARD',
    };
    forkJoin([
      this.flashcardService.getFlashCards$(payloadGetquestions),
            this.flashcardService.getPath$(pathPayload).pipe(
              catchError((err) => {
                return of('');
              })
            ),
    ]).subscribe(
      ([question, path]) => {
        this.FlashCards = question;
        this.path = path;
        console.log(this.FlashCards);
        this.droppedIndex = null;
        this.displayOrder = this.FlashCards.some((e) => e.Display_Order);
        this.sortFlashcards();
      },
      (error) => {
        // Handle errors gracefully
        console.error('Error fetching questions:', error);
        this.toaster.error('Failed to load questions. Please try again later.');
      }
    );
  }
  sortFlashcards() {
    const allNull = this.FlashCards.every((q) => q.Display_Order === null);
    if (allNull) {
      // Do nothing if all values are null
      return;
    }

    this.FlashCards.sort((a, b) => {
      // If both are null, maintain original order
      if (a.Display_Order === null && b.Display_Order === null) {
        return 0;
      }

      // If one is null, place it after the one with a value
      if (a.Display_Order === null) {
        return 1;
      }
      if (b.Display_Order === null) {
        return -1;
      }

      // Otherwise, sort by ascending Display_Order
      return a.Display_Order - b.Display_Order;
    });
  }

  getFlashCards() {
    const payloadGetquestions = {
      SubjectId: this.SubjectId,
      FlashcardID: this.FlashCardId,
      ScreenName: this.ScreenName,
    };
    this.flashcardService
      .getFlashCards$(payloadGetquestions)
      .subscribe((res) => {
        this.FlashCards = res;
        this.displayOrder = this.FlashCards.some((e) => e.Display_Order);
        this.sortFlashcards();
      });
  }

  dragEntered(event: CdkDragEnter<number>) {
    const drag = event.item;
    const dropList = event.container;
    const dragIndex = drag.data;
    const dropIndex = dropList.data;
    this.droppedIndex = dropIndex;
    this.dragDropInfo = { dragIndex, dropIndex };
    console.log('dragEntered', { dragIndex, dropIndex });

    const phContainer = dropList.element.nativeElement;
    const phElement = phContainer.querySelector('.cdk-drag-placeholder');

    if (phElement) {
      phContainer.removeChild(phElement);
      phContainer.parentElement?.insertBefore(phElement, phContainer);

      moveItemInArray(this.FlashCards, dragIndex, dropIndex);
    }
  }

  dragMoved(event: CdkDragMove<number>) {
    if (!this.dropListContainer || !this.dragDropInfo) return;

    const placeholderElement =
      this.dropListContainer.nativeElement.querySelector(
        '.cdk-drag-placeholder'
      );

    const receiverElement =
      this.dragDropInfo.dragIndex > this.dragDropInfo.dropIndex
        ? placeholderElement?.nextElementSibling
        : placeholderElement?.previousElementSibling;

    if (!receiverElement) {
      return;
    }

    receiverElement.style.display = 'none';
    this.dropListReceiverElement = receiverElement;
    const container = this.dropListContainer.nativeElement;
    const { y } = event.pointerPosition;

    const scrollSpeed = 50; // Increase speed for faster scrolling
    const threshold = 60; // Distance from the edge to trigger scrolling

    // Scroll up
    if (y < container.getBoundingClientRect().top + threshold) {
      container.scrollBy({ top: -scrollSpeed, behavior: 'smooth' });
    }

    // Scroll down
    if (y > container.getBoundingClientRect().bottom - threshold) {
      container.scrollBy({ top: scrollSpeed, behavior: 'smooth' });
    }
  }

  dragDropped(event: CdkDragDrop<number>) {
    if (!this.dropListReceiverElement) {
      return;
    }

    this.dropListReceiverElement.style.removeProperty('display');
    this.dropListReceiverElement = undefined;
    this.dragDropInfo = undefined;
  }

  SanitizeHTML(html: string) {
    return this.sanitizer.bypassSecurityTrustHtml(html);
  }

  isHighlighted(index: number): boolean {
    return index === this.droppedIndex; // Check if the tile should be highlighted
  }

  onSave() {
    const flashcardArray = this.FlashCards.map((element, index) => {
      return { FlashCardID: element.FlashCard_ID, DisplayOrder: index + 1 };
    });
    this.flashcardService
      .updateFlashCardDisplayOrder$(flashcardArray)
      .subscribe(
        (res) => {
            this.toaster.success('Saved Successfully', 'Success', {
              timeOut: ConstantVars.ToastSucessTimeout,
            });
          this.droppedIndex = null;
          this.getFlashCards();
        },
        (error) => {
          this.toaster.error(
            error.error.Message || 'Something went wrong',
            '',
            {
              timeOut: ConstantVars.ToastErrorTimeout,
            }
          );
        }
      );
  }
  onCross() {
    this.activeModal.close(false);
  }
  triggerParentFunction(flashcard: IFlashCard): void {
    // Emit data to the parent component
    const data = {
      FlashCard_ID: flashcard.FlashCard_ID,
      type: 'list',
      FlashcardList: this.FlashCards,
    };
    this.openFlashCard.emit(data);
  }

}
